import type {
  AddToCartData,
  ConversionEventData,
  LeadData,
  PurchaseData,
  RemoveFromCartData,
  SearchData,
  ViewContentData,
} from '@/events/conversion';

import type { ConversionEventOrigin, ConversionEventType } from '../contracts';
import {
  buildAddToCartDedupeKey,
  buildRemoveFromCartDedupeKey,
  CONVERSION_INTERACTION_WINDOW_MS,
  getInteractionBucket,
} from './AddToCartDedupe';

export { CONVERSION_INTERACTION_WINDOW_MS, getInteractionBucket };

const CLICK_ORIGINS = new Set<string>([
  'manual',
  'auto:selector',
  'auto:text',
  'auto:checkout',
  'auto:purchase',
  'auto:lead',
  'auto:addToCart',
  'auto:removeFromCart',
  'auto:initiateCheckout',
  'auto:addPaymentInfo',
  'auto:search',
  'auto:viewContent',
]);

function isUrlOrigin(origin: ConversionEventOrigin): boolean {
  return origin === 'auto:url' || origin.startsWith('auto:url:');
}

function isInteractionOrigin(origin: ConversionEventOrigin): boolean {
  if (isUrlOrigin(origin)) return false;
  if (CLICK_ORIGINS.has(origin)) return true;
  // Sources Etapa 14: auto:form:… / auto:selector:… / auto:lifecycle:…
  return (
    origin.startsWith('auto:form:') ||
    origin.startsWith('auto:selector:') ||
    origin.startsWith('auto:lifecycle:')
  );
}

function secondBucket(now = Date.now()): number {
  return Math.floor(now / 1000);
}

function currentPath(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

/**
 * Chave canônica de dedupe — sem origem (exceto fallbacks específicos).
 * Garante 1 evento por tipo/interação mesmo com múltiplos detectores.
 */
export function buildCanonicalDedupeKey(
  type: ConversionEventType,
  data: ConversionEventData | undefined,
  origin: ConversionEventOrigin,
  sessionId: string,
  now = Date.now()
): string {
  const payload = data ?? {};
  const bucket = getInteractionBucket(now, CONVERSION_INTERACTION_WINDOW_MS);

  switch (type) {
    case 'PURCHASE': {
      const purchase = payload as PurchaseData;
      if (purchase.orderId) {
        return `PURCHASE:order:${purchase.orderId}`;
      }
      if (isUrlOrigin(origin)) {
        return `PURCHASE:url:${currentPath()}`;
      }
      if (isInteractionOrigin(origin)) {
        return `PURCHASE:interaction:${bucket}`;
      }
      return `PURCHASE:manual:${secondBucket(now)}`;
    }
    case 'LEAD': {
      const lead = payload as LeadData;
      const identity = `${lead.email ?? ''}:${lead.phone ?? ''}`;
      if (identity !== ':') {
        return `LEAD:identity:${identity}`;
      }
      return `LEAD:interaction:${bucket}`;
    }
    case 'ADD_TO_CART':
      return buildAddToCartDedupeKey(
        payload as AddToCartData,
        now,
        CONVERSION_INTERACTION_WINDOW_MS
      );
    case 'REMOVE_FROM_CART':
      return buildRemoveFromCartDedupeKey(
        payload as RemoveFromCartData,
        now,
        CONVERSION_INTERACTION_WINDOW_MS
      );
    case 'INITIATE_CHECKOUT': {
      if (isUrlOrigin(origin)) {
        return `INITIATE_CHECKOUT:url:${currentPath()}`;
      }
      return `INITIATE_CHECKOUT:interaction:${bucket}`;
    }
    case 'ADD_PAYMENT_INFO':
      return `ADD_PAYMENT_INFO:session:${sessionId}`;
    case 'VIEW_CONTENT': {
      const view = payload as ViewContentData;
      if (view.contentId) {
        return `VIEW_CONTENT:content:${view.contentId}`;
      }
      return `VIEW_CONTENT:interaction:${bucket}`;
    }
    case 'SEARCH': {
      const search = payload as SearchData;
      return `SEARCH:query:${search.query}`;
    }
    default:
      return `${type}:interaction:${bucket}`;
  }
}
